// Story 11.1 — Página del editor. Layout split: árbol izquierdo + tabs+editor
// centro + panel de problemas derecho colapsable. Bloqueada en mobile vía
// `DesktopOnlyNotice` (Story 10.5).
// Story 11.2 — agrega panel del FlowDiagram.
// Story 11.3 — sync editor↔diagrama (cursor → highlight; click → revealLine).
// Story 11.4 — agrega LogPanel inferior + EditorToolbar superior + dialogs.

import { useParams } from '@tanstack/react-router';
import { AlertTriangle, Eye, EyeOff, Loader2, Pin, Save } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { toast } from 'sonner';
import { Can } from '@/components/auth/Can';
import { CodeEditor, type CodeEditorHandle } from '@/components/editor/CodeEditor';
import { FlowDiagram } from '@/components/editor/diagram/FlowDiagram';
import { DeployDialog } from '@/components/editor/dialogs/DeployDialog';
import { DevModeInfoDialog } from '@/components/editor/dialogs/DevModeInfoDialog';
import { RollbackDialog } from '@/components/editor/dialogs/RollbackDialog';
import { SaveAsTemplateDialog } from '@/components/editor/dialogs/SaveAsTemplateDialog';
import { EditorTabs } from '@/components/editor/EditorTabs';
import { EditorToolbar } from '@/components/editor/EditorToolbar';
import { FileTree } from '@/components/editor/FileTree';
import { LogPanel, type LogPanelHandle } from '@/components/editor/logs/LogPanel';
import { ProblemsPanel } from '@/components/editor/ProblemsPanel';
import { DesktopOnlyNotice } from '@/components/layout/DesktopOnlyNotice';
import { Button } from '@/components/ui/button';
import { useBot } from '@/hooks/queries/use-bots';
import { useFileContent, useFiles } from '@/hooks/queries/use-files';
import { useEditorDiagramSync } from '@/hooks/use-editor-diagram-sync';
import { useFlowFromActiveTab } from '@/hooks/use-flow-from-active-tab';
import { useSaveFile } from '@/hooks/use-save-file';
import type { LayoutPreset } from '@/lib/editor-diagram-prefs';
import { isDirty, MAX_TABS } from '@/lib/editor-tabs-reducer';
import { languageForPath, type ValidationReport } from '@/lib/editor-types';
import { isFlowPath } from '@/lib/flow-extraction';
import { cn } from '@/lib/utils';
import {
  DIAGRAM_MAX_PCT,
  DIAGRAM_MIN_PCT,
  LOGS_MAX_PCT,
  LOGS_MIN_PCT,
  useEditorStore,
} from '@/stores/editor-store';
import { useValidationStore } from '@/stores/validation-store';

export function EditorPage(): React.ReactElement {
  const params = useParams({ strict: false }) as { botId?: string };
  const botId = Number(params.botId);
  return (
    <DesktopOnlyNotice minWidth={1024}>
      {/* P-333 (review 11.1): defense-in-depth. El router guard de Story 10.5
          ya bloquea el acceso, pero un refactor o cache stale del `me` query
          podría dejar pasar a un user sin `edit_bot`. El `<Can>` es un
          chequeo en el componente — fail-closed: si la capability no está,
          render fallback en lugar de Monaco. */}
      <Can
        capability="edit_bot"
        botId={botId}
        fallback={
          <div className="flex h-full items-center justify-center p-6">
            <p className="text-sm text-muted-foreground">
              No tienes permiso para editar bots. Contacta a un administrador.
            </p>
          </div>
        }
      >
        <EditorPageDesktop />
      </Can>
    </DesktopOnlyNotice>
  );
}

function EditorPageDesktop(): React.ReactElement {
  const params = useParams({ strict: false }) as { botId?: string };
  const botId = Number(params.botId);

  const { data: bot } = useBot(botId, Number.isFinite(botId));
  const filesQuery = useFiles(botId);

  const tabs = useEditorStore((s) => s.tabs);
  const activePath = useEditorStore((s) => s.activePath);
  const openTab = useEditorStore((s) => s.openTab);
  const closeTab = useEditorStore((s) => s.closeTab);
  const setActive = useEditorStore((s) => s.setActive);
  const updateContent = useEditorStore((s) => s.updateContent);

  const fileQuery = useFileContent(botId, activePath);
  const saveFile = useSaveFile();

  const diagramVisible = useEditorStore((s) => s.diagramVisible);
  const diagramSize = useEditorStore((s) => s.diagramSize);
  const toggleDiagram = useEditorStore((s) => s.toggleDiagram);
  const setDiagramSize = useEditorStore((s) => s.setDiagramSize);

  // Story 11.4 — LogPanel state + dialogs.
  const logsCollapsed = useEditorStore((s) => s.logsCollapsed);
  const logsPanelSize = useEditorStore((s) => s.logsPanelSize);
  const toggleLogs = useEditorStore((s) => s.toggleLogs);
  const setLogsPanelSize = useEditorStore((s) => s.setLogsPanelSize);
  const applyLayoutPreset = useEditorStore((s) => s.applyLayoutPreset);

  const logPanelRef = useRef<LogPanelHandle | null>(null);
  // P-438 (review 11.4): cuando `logsCollapsed=true`, el `<LogPanel>` no
  // está montado → `logPanelRef.current` es null → `pushLocal` se perdía
  // silenciosamente. Encolamos en una ref; el effect que dispara cuando
  // `logsCollapsed` pasa a false drena la queue contra el ref del panel
  // recién montado (defer via `queueMicrotask` para esperar el mount).
  type PushLocalArg = Parameters<NonNullable<LogPanelHandle['pushLocal']>>[0];
  const pendingLogsRef = useRef<PushLocalArg[]>([]);

  const [deployOpen, setDeployOpen] = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [devInfoOpen, setDevInfoOpen] = useState(false);

  function pushLocalLog(entry: PushLocalArg): void {
    if (!logsCollapsed && logPanelRef.current) {
      logPanelRef.current.pushLocal(entry);
      return;
    }
    pendingLogsRef.current.push(entry);
    if (logsCollapsed) toggleLogs(); // mounta el panel; el effect drena
  }

  // Drena la queue cuando el panel pasa a visible. `queueMicrotask` espera
  // el mount completo (handle assigned via `useImperativeHandle`).
  useEffect(() => {
    if (logsCollapsed) return;
    if (pendingLogsRef.current.length === 0) return;
    const queued = pendingLogsRef.current;
    pendingLogsRef.current = [];
    queueMicrotask(() => {
      for (const entry of queued) logPanelRef.current?.pushLocal(entry);
    });
  }, [logsCollapsed]);

  function handleApplyLayout(preset: LayoutPreset): void {
    applyLayoutPreset(preset);
  }

  // P-472 (review 11.5): el `validation-store` es global; cuando el user
  // cambia de bot, errors/warnings de bot anterior persistían como si
  // fueran del actual. Reset al montar el editor de cada bot. `botId` está
  // en deps a propósito como trigger aunque el body no lo lea.
  const resetValidation = useValidationStore((s) => s.reset);
  // biome-ignore lint/correctness/useExhaustiveDependencies: botId es trigger del reset
  useEffect(() => {
    resetValidation();
  }, [botId, resetValidation]);

  // P-466 (review 11.5): extraemos `saveAllDirty()` como función nombrada
  // para que el atajo Cmd+Shift+S y el `revel:command-save-all` event la
  // invoquen directo. El approach previo (sintetizar un `KeyboardEvent`
  // y dispatchearlo) era frágil — depende de que el handler de atajos
  // exista, lea `target` correctamente, etc.
  function saveAllDirty(): void {
    const dirty = tabs.filter((t) => isDirty(t) && !t.readOnly);
    if (dirty.length === 0) {
      toast.info('No hay archivos con cambios.');
      return;
    }
    for (const t of dirty) {
      saveFile.mutate({ botId, path: t.path, content: t.content });
    }
  }

  // Story 11.5 — listen for command palette events. La paleta es global y
  // no conoce el state local de los dialogs; usa custom events.
  // biome-ignore lint/correctness/useExhaustiveDependencies: handler closures
  useEffect(() => {
    function onDeployCmd() {
      setDeployOpen(true);
    }
    function onRollbackCmd() {
      setRollbackOpen(true);
    }
    function onSaveAllCmd() {
      saveAllDirty();
    }
    window.addEventListener('revel:command-deploy', onDeployCmd);
    window.addEventListener('revel:command-rollback', onRollbackCmd);
    window.addEventListener('revel:command-save-all', onSaveAllCmd);
    return () => {
      window.removeEventListener('revel:command-deploy', onDeployCmd);
      window.removeEventListener('revel:command-rollback', onRollbackCmd);
      window.removeEventListener('revel:command-save-all', onSaveAllCmd);
    };
  }, [tabs, botId]);

  const flowFromTab = useFlowFromActiveTab();

  const [problemsOpen, setProblemsOpen] = useState(true);
  const [lastReport, setLastReport] = useState<ValidationReport | null>(null);

  // Story 11.3 — sticky last-flow: cuando la tab activa es un flow,
  // recordamos el path. Si el user salta a `bot.yaml` o cualquier no-flow,
  // el diagrama sigue mostrando el último flow visto. State per-session
  // (resetea al refresh).
  const [lastFlowPath, setLastFlowPath] = useState<string | null>(null);
  useEffect(() => {
    if (activePath && isFlowPath(activePath)) setLastFlowPath(activePath);
  }, [activePath]);

  const stickyTab = useMemo(
    () => (lastFlowPath ? (tabs.find((t) => t.path === lastFlowPath) ?? null) : null),
    [tabs, lastFlowPath],
  );

  // Sync hook: usa el contenido del flow activo (o sticky) para construir
  // el lineMap. Cuando el user está en `bot.yaml`, el sync queda "frozen"
  // sobre el último flow visualizado — el lineMap sigue siendo del sticky
  // así el highlight no parpadea al cambiar de tab.
  // P-401 (review 11.3): pareamos el lineMap con la MISMA fuente que el
  // diagrama (`baseline`, no `content`). El diagrama parsea baseline en
  // `useFlowFromActiveTab` (P-372 de 11.2 — re-render solo on-save) →
  // los node ids `step-${index}` derivan de baseline. Si lineMap usaba
  // `content` (live), un step recién agregado en dirty desplaza los
  // índices y el highlight apuntaba a un nodo inexistente. Usar baseline
  // mantiene la coherencia: cursor en la zona dirty no highlighteará
  // nada (es expected — el diagrama no muestra esos cambios todavía).
  const isOnFlowTab = activePath != null && isFlowPath(activePath);
  const activeFlowTab = isOnFlowTab ? (tabs.find((t) => t.path === activePath) ?? null) : null;
  const syncContent = isOnFlowTab
    ? (activeFlowTab?.baseline ?? activeFlowTab?.content ?? '')
    : (stickyTab?.baseline ?? stickyTab?.content ?? '');
  // P-413: reset el lastValidMap cuando cambia el path (cross-file leak).
  const sync = useEditorDiagramSync(syncContent, activePath);

  // Story 11.3 — flow para el FlowDiagram. Cuando el user está en el flow
  // tab, usamos `flowFromTab.flow`. Cuando no, parseamos el sticky tab via
  // `parseFlowYaml` (dynamic import de yaml lib).
  const [stickyFlow, setStickyFlow] = useState<typeof flowFromTab>({
    flow: null,
    notAFlow: false,
    error: null,
  });
  useEffect(() => {
    if (isOnFlowTab || !stickyTab) {
      setStickyFlow({ flow: null, notAFlow: false, error: null });
      return;
    }
    let cancelled = false;
    void (async () => {
      const { parseFlowYaml } = await import('@/lib/flow-extraction');
      const { flowNameFromPath } = await import('@/lib/flow-extraction');
      const res = await parseFlowYaml(stickyTab.content, flowNameFromPath(stickyTab.path));
      if (!cancelled) setStickyFlow({ flow: res.flow, notAFlow: false, error: res.error });
    })();
    return () => {
      cancelled = true;
    };
  }, [isOnFlowTab, stickyTab]);

  const diagramFlow = isOnFlowTab ? flowFromTab : stickyFlow;

  const editorRef = useRef<CodeEditorHandle | null>(null);
  // P-407 (review 11.3): cross-tab click race. `setTimeout(0)` no esperaba
  // que Monaco terminara de montarse en cold-load (chunk + handleMount son
  // async) → `editorRef.current` era null y el revealLine no-op silencioso.
  // Solución: enqueue el line target en una ref y disparar desde un
  // `useEffect` que corre cuando la tab activa cambió + editor está ready
  // (callback `onReady` desde CodeEditor — ver más abajo).
  const pendingRevealLineRef = useRef<number | null>(null);

  function tryFlushPendingReveal(): void {
    const line = pendingRevealLineRef.current;
    if (line == null) return;
    if (editorRef.current) {
      editorRef.current.revealLine(line);
      pendingRevealLineRef.current = null;
    }
  }

  function handleStepClick(stepId: string): void {
    const targetLine = sync.stepToLine.get(stepId);
    if (targetLine == null) return;
    // Si el step click vino del sticky flow pero el user está en otra tab,
    // primero saltamos a esa tab. Encolamos el revealLine — `onEditorReady`
    // (callback desde CodeEditor en handleMount) lo flushea cuando el
    // editor de la tab nueva esté montado. P-407 cubre el caso de chunk
    // load lento.
    if (!isOnFlowTab && lastFlowPath) {
      pendingRevealLineRef.current = targetLine;
      setActive(lastFlowPath);
      // El effect de [activePath] limpiará el ref si la nueva tab estaba
      // ya montada y `onReady` ya disparó.
      tryFlushPendingReveal();
      return;
    }
    editorRef.current?.revealLine(targetLine);
  }

  // Carga el contenido en el tab cuando llega del backend.
  // P-341 (review 11.1): cuando el user clickeó un path, abrimos un
  // placeholder con `content: ''` para que `useFileContent` dispare. Cuando
  // llega data, el tab YA existe → necesitamos REPLACE el contenido, no
  // skipear. Detectamos placeholder por `baseline === '' && content === ''`.
  useEffect(() => {
    if (!fileQuery.data) return;
    const target = fileQuery.data.path;
    const existing = tabs.find((t) => t.path === target);
    if (existing) {
      const isPlaceholder = existing.baseline === '' && existing.content === '';
      if (!isPlaceholder) return;
      // Reabrir con el content real — el reducer detecta path existente y
      // setea baseline/content via re-open con `now` updated. Trick: usamos
      // closeTab + openTab para forzar baseline reset.
      closeTab(target);
    }
    openTab({
      path: target,
      content: fileQuery.data.content,
      readOnly: fileQuery.data.readOnly,
    });
  }, [fileQuery.data, openTab, closeTab, tabs]);

  // P-343 (review 11.1): el reporte se limpia al cambiar de archivo activo
  // (los problems mostrados siempre corresponden al último save de ESE
  // archivo). Antes el dep array era `[]` → solo corría en mount, dejando
  // el badge de errores stale al cambiar de tab. `setLastReport` es estable
  // (useState setter), no necesita estar en deps.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setter estable
  useEffect(() => {
    setLastReport(null);
  }, [activePath]);

  function handleSelect(path: string): void {
    const exists = tabs.find((t) => t.path === path);
    if (exists) {
      setActive(path);
      return;
    }
    // P-340 (review 11.1): si llegamos al cap con TODOS los tabs dirty, el
    // reducer evictaría el oldest dirty silenciosamente — data loss. Pedir
    // confirmación antes de abrir el placeholder. El reducer sigue siendo
    // puro; la UX advertencia vive acá según diseño.
    if (tabs.length >= MAX_TABS && tabs.every(isDirty)) {
      const proceed = window.confirm(
        `Tenés ${MAX_TABS} archivos abiertos y todos con cambios sin guardar.\n` +
          'Abrir uno nuevo descartará los cambios del más antiguo. ¿Continuar?',
      );
      if (!proceed) return;
    }
    // Placeholder optimista para que `useFileContent` (que depende de
    // `activePath`) dispare. Cuando llega data, el effect de arriba lo
    // reemplaza con el content real (P-341).
    openTab({ path, content: '', readOnly: false });
  }

  const activeTab = useMemo(
    () => tabs.find((t) => t.path === activePath) ?? null,
    [tabs, activePath],
  );

  function handleSave(): void {
    if (!activeTab || activeTab.readOnly) return;
    saveFile.mutate(
      { botId, path: activeTab.path, content: activeTab.content },
      {
        onSuccess: (result) => {
          setLastReport(result.validation);
          if (!result.validation.ok) setProblemsOpen(true);
        },
      },
    );
  }

  function handleSaveAll(): void {
    const dirty = tabs.filter((t) => isDirty(t) && !t.readOnly);
    if (dirty.length === 0) {
      toast.info('No hay archivos con cambios.');
      return;
    }
    for (const t of dirty) {
      saveFile.mutate({ botId, path: t.path, content: t.content });
    }
  }

  // Atajos globales (Spec AC #3 / #7 / Task 5.2 — P-334/P-335 + Story 11.2/11.4):
  //   Cmd+\`        → toggle problems panel
  //   Cmd+Shift+D   → toggle diagram panel (Story 11.2)
  //   Cmd+J         → toggle log panel (Story 11.4 — VSCode-like)
  //   Cmd+W         → cerrar tab activa
  //   Cmd+Shift+S   → save all dirty tabs
  //   Cmd+1..Cmd+9  → cambiar a tab N
  // Cmd+S sigue manejado por Monaco (`editor.addCommand` en CodeEditor) —
  // solo cuando el editor tiene foco. Si el foco está afuera y el user
  // pega Cmd+S, el browser save dialog se mantiene; aceptable porque la
  // toolbar tiene un botón "Guardar" siempre visible.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const cmd = e.metaKey || e.ctrlKey;
      if (!cmd) return;
      // P-433 (review 11.4): cuando el foco está en un input/textarea/
      // contenteditable (Deploy notes, log search, version field, etc.),
      // los Cmd+J/Cmd+W/Cmd+1..9/Cmd+Shift+S NO deben interceptarse —
      // rompen typing legítimo. Esc y Cmd+S siguen manejados por
      // diálogos/Monaco directamente. Cmd+\` (toggle problems) también
      // se exime acá: dentro de un editor, el foco va al editor; el
      // shortcut sigue funcionando porque Monaco no captura `.
      const target = e.target as HTMLElement | null;
      const inEditable =
        target != null &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      if (inEditable) return;
      if (e.key === '`') {
        e.preventDefault();
        setProblemsOpen((v) => !v);
        return;
      }
      if (e.shiftKey && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        toggleDiagram();
        return;
      }
      if (!e.shiftKey && (e.key === 'j' || e.key === 'J')) {
        // Story 11.4 — Cmd+J toggle log panel. Sin shift; con shift queda
        // libre para futuros atajos.
        e.preventDefault();
        toggleLogs();
        return;
      }
      if (e.key === 'w' || e.key === 'W') {
        if (!activePath) return;
        e.preventDefault();
        closeTab(activePath);
        return;
      }
      if (e.shiftKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        // Inline para que el listener tenga `tabs`/`saveFile` frescos del
        // cierre del effect — `handleSaveAll` arriba duplica esta lógica
        // para el botón visible. Mantener sincronizadas si cambian.
        const dirty = tabs.filter((t) => isDirty(t) && !t.readOnly);
        if (dirty.length === 0) {
          toast.info('No hay archivos con cambios.');
          return;
        }
        for (const t of dirty) {
          saveFile.mutate({ botId, path: t.path, content: t.content });
        }
        return;
      }
      // Cmd+1..9 → activar tab N (1-based; 9 último incluso si más de 9).
      if (e.key >= '1' && e.key <= '9') {
        const idx = Number(e.key) - 1;
        const target = tabs[idx];
        if (!target) return;
        e.preventDefault();
        setActive(target.path);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activePath, tabs, closeTab, setActive, saveFile, botId, toggleDiagram, toggleLogs]);

  // Story 11.2 + P-362: diagrama "outdated" cuando hay edits sin guardar
  // O el último save falló validación. Diferenciamos los dos casos para
  // que el copy del overlay sea preciso (`dirty` vs `validation-failed`).
  const diagramOutdated = useMemo(() => {
    if (activeTab && isDirty(activeTab)) return true;
    return lastReport != null && !lastReport.ok;
  }, [activeTab, lastReport]);

  const diagramOutdatedReason: 'dirty' | 'validation-failed' | null = useMemo(() => {
    if (lastReport != null && !lastReport.ok) return 'validation-failed';
    if (activeTab && isDirty(activeTab)) return 'dirty';
    return null;
  }, [activeTab, lastReport]);

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      {/* Story 11.4 — top toolbar con bot status + Deploy/Rollback/DevMode. */}
      <EditorToolbar
        bot={bot}
        onDeploy={() => setDeployOpen(true)}
        onRollback={() => setRollbackOpen(true)}
        onDevModeInfo={() => setDevInfoOpen(true)}
        onApplyLayout={handleApplyLayout}
        onSaveAsTemplate={() => setSaveAsTemplateOpen(true)}
        onShowValidationErrors={() => {
          // Story 11.5: abre el LogPanel para que el user vea los errors
          // de validación. El filtrado fino lo hace el user con el search.
          if (logsCollapsed) toggleLogs();
        }}
      />
      {/* Sub-toolbar con controles del editor (Save/Problems/Diagrama/Logs). */}
      <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5">
        <p className="text-xs text-muted-foreground">
          Cmd+S guardar · Cmd+Shift+S guardar todo · Cmd+` Problemas · Cmd+J Logs · Cmd+Shift+D
          Diagrama
        </p>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="default"
            onClick={handleSave}
            disabled={!activeTab || activeTab.readOnly || saveFile.isPending || !isDirty(activeTab)}
          >
            {saveFile.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Guardar
          </Button>
          {(() => {
            const dirtyCount = tabs.filter((t) => isDirty(t) && !t.readOnly).length;
            if (dirtyCount < 2) return null;
            return (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSaveAll}
                disabled={saveFile.isPending}
                title="Cmd+Shift+S"
              >
                <Save className="h-4 w-4" />
                Guardar todo ({dirtyCount})
              </Button>
            );
          })()}
          <Button
            size="sm"
            variant={problemsOpen ? 'default' : 'outline'}
            onClick={() => setProblemsOpen((v) => !v)}
            aria-pressed={problemsOpen}
          >
            <AlertTriangle className="h-4 w-4" />
            Problemas
            {lastReport && lastReport.errors.length > 0 && (
              <span className="ml-1 rounded bg-destructive/20 px-1 text-xs">
                {lastReport.errors.length}
              </span>
            )}
          </Button>
          <Button
            size="sm"
            variant={diagramVisible ? 'default' : 'outline'}
            onClick={toggleDiagram}
            aria-pressed={diagramVisible}
            aria-label="Mostrar/ocultar diagrama"
            title="Cmd+Shift+D"
          >
            {diagramVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            Diagrama
          </Button>
          <Button
            size="sm"
            variant={!logsCollapsed ? 'default' : 'outline'}
            onClick={toggleLogs}
            aria-pressed={!logsCollapsed}
            aria-label="Mostrar/ocultar logs"
            title="Cmd+J"
          >
            {!logsCollapsed ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            Logs
          </Button>
        </div>
      </header>

      {/* Story 11.4 — wrapper vertical: editor (top) + LogPanel (bottom). */}
      <PanelGroup
        direction="vertical"
        autoSaveId={null}
        className="min-h-0 flex-1"
        onLayout={(sizes: number[]) => {
          // Si el LogPanel es visible, su tamaño es el último elemento.
          if (!logsCollapsed && sizes.length >= 2) {
            const logsSize = sizes[sizes.length - 1];
            if (typeof logsSize === 'number') setLogsPanelSize(logsSize);
          }
        }}
      >
        <Panel defaultSize={logsCollapsed ? 100 : 100 - logsPanelSize}>
          <div className="flex h-full min-h-0 flex-1 gap-0 p-1">
            <PanelGroup
              direction="horizontal"
              autoSaveId={null}
              onLayout={(sizes: number[]) => {
                // Story 11.2 — el panel del diagrama es el último (cuando visible).
                if (diagramVisible && sizes.length >= 3) {
                  const last = sizes[sizes.length - 1];
                  if (typeof last === 'number') setDiagramSize(last);
                }
              }}
            >
              <Panel defaultSize={20} minSize={15} maxSize={30}>
                <aside className="h-full overflow-y-auto rounded-md border border-border bg-card">
                  <FileTree
                    tree={filesQuery.data}
                    selectedPath={activePath}
                    onSelect={handleSelect}
                    isLoading={filesQuery.isLoading}
                  />
                </aside>
              </Panel>
              <PanelResizeHandle className="w-1.5 bg-transparent transition-colors hover:bg-border" />
              <Panel
                defaultSize={problemsOpen ? (diagramVisible ? 30 : 60) : diagramVisible ? 50 : 80}
              >
                <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-border bg-background">
                  <EditorTabs
                    tabs={tabs}
                    activePath={activePath}
                    onActivate={setActive}
                    onClose={closeTab}
                  />
                  {activeTab ? (
                    <div className="flex-1">
                      {activeTab.readOnly && activeTab.path.startsWith('ext/') && (
                        <div className="border-b border-border bg-warning/10 px-3 py-1.5 text-xs text-warning">
                          Edición de TypeScript pendiente para Fase 2 — usá la CLI por ahora.
                        </div>
                      )}
                      <CodeEditor
                        key={activeTab.path}
                        ref={editorRef}
                        path={activeTab.path}
                        language={languageForPath(activeTab.path)}
                        value={activeTab.content}
                        readOnly={activeTab.readOnly}
                        onChange={(next) => updateContent(activeTab.path, next)}
                        onSave={handleSave}
                        problems={lastReport?.errors}
                        warnings={lastReport?.warnings}
                        onCursorChange={(line) => sync.reportCursorPosition(line)}
                        // P-407: cuando el editor termina de montar, flush
                        // cualquier revealLine pendiente del cross-tab click.
                        onReady={tryFlushPendingReveal}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                      Selecciona un archivo en el árbol para empezar.
                    </div>
                  )}
                </section>
              </Panel>
              {problemsOpen && (
                <>
                  <PanelResizeHandle className="w-1.5 bg-transparent transition-colors hover:bg-border" />
                  <Panel defaultSize={20} minSize={15} maxSize={35}>
                    <ProblemsPanel
                      report={lastReport}
                      onClose={() => setProblemsOpen(false)}
                      onJump={(_path) => {
                        // El editor por ahora se posiciona en línea 1; Story 11.x
                        // pode cablear `editor.revealLineInCenter` cuando salga.
                      }}
                    />
                  </Panel>
                </>
              )}
              {diagramVisible && (
                <>
                  <PanelResizeHandle className="w-1.5 bg-transparent transition-colors hover:bg-border" />
                  <Panel
                    defaultSize={diagramSize}
                    minSize={DIAGRAM_MIN_PCT}
                    maxSize={DIAGRAM_MAX_PCT}
                  >
                    <div
                      className={cn(
                        'relative h-full overflow-hidden rounded-md border border-border bg-card',
                        'transition-[opacity] duration-200',
                      )}
                    >
                      <FlowDiagram
                        flow={diagramFlow.flow}
                        notAFlow={
                          isOnFlowTab
                            ? diagramFlow.notAFlow
                            : stickyTab == null /* sin sticky → placeholder */
                        }
                        errorMessage={diagramFlow.error}
                        outdated={diagramOutdated}
                        outdatedReason={diagramOutdatedReason}
                        highlightedStepId={sync.highlightedStepId}
                        onStepClick={handleStepClick}
                      />
                      {!isOnFlowTab && stickyTab && (
                        /* Story 11.3 AC #3: sticky last-flow indicator. */
                        <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-card/95 px-2 py-1 text-xs text-muted-foreground shadow">
                          <Pin className="h-3 w-3" aria-hidden />
                          Mostrando: {stickyTab.path}
                        </div>
                      )}
                    </div>
                  </Panel>
                </>
              )}
            </PanelGroup>
          </div>
        </Panel>
        {!logsCollapsed && (
          <>
            <PanelResizeHandle className="h-1.5 bg-transparent transition-colors hover:bg-border" />
            <Panel defaultSize={logsPanelSize} minSize={LOGS_MIN_PCT} maxSize={LOGS_MAX_PCT}>
              <LogPanel
                ref={logPanelRef}
                botId={Number.isFinite(botId) ? botId : undefined}
                onClose={toggleLogs}
              />
            </Panel>
          </>
        )}
      </PanelGroup>

      <DeployDialog
        open={deployOpen}
        onClose={() => setDeployOpen(false)}
        bot={bot}
        pushLocalLog={pushLocalLog}
        onShowValidationErrors={() => {
          // Cierra el dialog y abre el LogPanel para revisar todos los errors.
          setDeployOpen(false);
          if (logsCollapsed) toggleLogs();
        }}
      />
      <RollbackDialog
        open={rollbackOpen}
        onClose={() => setRollbackOpen(false)}
        bot={bot}
        pushLocalLog={pushLocalLog}
      />
      <SaveAsTemplateDialog
        open={saveAsTemplateOpen}
        onClose={() => setSaveAsTemplateOpen(false)}
        bot={bot}
      />
      <DevModeInfoDialog
        open={devInfoOpen}
        onClose={() => setDevInfoOpen(false)}
        botName={bot?.name}
      />
    </div>
  );
}
